import { Grid, Typography } from '@mui/material';
import { isNotNil, uniq } from 'es-toolkit';

import { parseNullableValue } from '@clearverify/utils';

import { DataDisputeReportInfoFragment } from 'src/api';
import { DemoPrivacyAvatar, DemoPrivacyText } from 'src/components/demoPrivacyMode';
import { InboxItemStatusChip } from 'src/components/inboxItem';
import { TableColumn } from 'src/components/table';
import { SxProp } from 'src/types/mui';
import { checkDataDisputeItemStatus } from 'src/utils/dataDispute';
import { formatMaskedSsnNumber } from 'src/utils/ssn';

export const DATA_DISPUTE_REPORT_TABLE_CONSUMER_COLUMN: TableColumn<DataDisputeReportInfoFragment> =
  {
    title: 'Employee',
    key: 'employee.user.firstName',
    sortable: true,
    render: (_valueByDataPath, dataDisputeReport) => {
      const { last4OfSsn, user } = dataDisputeReport.employee;

      return (
        <Grid sx={avatarStyles}>
          <DemoPrivacyAvatar src={user?.avatar?.downloadUrl} variant="circular" />
          <Typography variant="body2" ml={1}>
            <DemoPrivacyText>
              {[formatMaskedSsnNumber(last4OfSsn), user?.fullName].filter(isNotNil).join(': ')}
            </DemoPrivacyText>
          </Typography>
        </Grid>
      );
    },
  };

export const DATA_DISPUTE_REPORT_TABLE_CONSUMER_EMAIL_COLUMN: TableColumn<DataDisputeReportInfoFragment> =
  {
    title: 'Employee Email',
    key: 'employee.user.email',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, dataDisputeReport) => (
      <DemoPrivacyText>
        {parseNullableValue(dataDisputeReport.employee.user?.email)}
      </DemoPrivacyText>
    ),
  };

export const DATA_DISPUTE_REPORT_TABLE_EMPLOYERS_COLUMN: TableColumn<DataDisputeReportInfoFragment> =
  {
    title: 'Employers',
    key: 'employers',
    render: (_valueByDataPath, dataDisputeReport) => (
      <DemoPrivacyText>
        {dataDisputeReport.dataDisputeItemsList
          .map(dataDisputeItem => dataDisputeItem.employer.displayName)
          .join(', ')}
      </DemoPrivacyText>
    ),
  };

export const DATA_DISPUTE_REPORT_TABLE_PEO_COLUMN: TableColumn<DataDisputeReportInfoFragment> = {
  title: 'PEO',
  key: 'peo',
  render: (_valueByDataPath, dataDisputeReport) => (
    <DemoPrivacyText>
      {parseNullableValue(
        uniq(
          dataDisputeReport.dataDisputeItemsList.map(
            dataDisputeItem => dataDisputeItem.employer.peo?.displayName,
          ),
        )
          .filter(isNotNil)
          .join(', '),
      )}
    </DemoPrivacyText>
  ),
};

export const DATA_DISPUTE_REPORT_TABLE_STATUS_COLUMN: TableColumn<DataDisputeReportInfoFragment> = {
  title: 'Status',
  key: 'dataDisputeReportStatus',
  render: (_valueByDataPath, dataDisputeReport) => (
    <InboxItemStatusChip
      status={checkDataDisputeItemStatus(dataDisputeReport)}
      isVerificationRequest={false}
    />
  ),
};

const avatarStyles: SxProp = {
  display: 'flex',
  alignItems: 'center',
};
